/**
 * ESI ID (ERCOT Electric Service Identifier) input helpers for the "Shop by
 * ESI ID" sub-flow (SHOP-3).
 *
 * ── CONTRACT NOW OWNS THE WIRE SHAPE ───────────────────────────────────────
 * SHOP-1 landed `EsiidLookupRequestSchema` / `EsiidLookupResponseSchema` /
 * `MeterDetail` in `@rheora/enrollment-contract` (meter.ts). This file no longer
 * forward-specs them — it RE-EXPORTS the contract schemas so callers import from
 * one place, and keeps only the browser-side UX helpers (strip/validate before a
 * captcha challenge) that do not belong on the wire.
 *
 * ── AC3 POSTURE ────────────────────────────────────────────────────────────
 * A raw ESI ID typed by the shopper is display/lookup INPUT — the BFF resolves it
 * server-side against the funnel session and hands back an OPAQUE `candidateId`.
 * Validating format here is a UX affordance (block obviously-bad input BEFORE a
 * captcha challenge), NOT an authorization decision; the BFF re-validates.
 */

export {
  EsiidLookupRequestSchema,
  EsiidLookupResponseSchema,
  MeterDetailSchema,
  type EsiidLookupRequest,
  type EsiidLookupResponse,
  type MeterDetail,
} from '@rheora/enrollment-contract';

/** ERCOT accepts 10..22 digits — the same bounds the contract's request regex enforces. */
export const ESIID_MIN_DIGITS = 10;
export const ESIID_MAX_DIGITS = 22;

/**
 * Strip everything except digits — the shopper may paste "10-4437-2000-1234-5"
 * or "1044 3720 0012 3451 2" from a bill. Letters DO NOT survive the strip, so
 * the downstream digit-count check rejects any input that carried them.
 */
export function stripEsiidInput(raw: string): string {
  return raw.replace(/\D/gu, '');
}

/**
 * True when the RAW (unstripped) input contains a non-digit / non-separator
 * character — used by the form to show a helpful "digits only" hint.
 */
export function hasEsiidLetters(raw: string): boolean {
  return /[^\d\s-]/u.test(raw);
}

/** True when the STRIPPED digit string is a well-formed ESI ID (10..22 digits). */
export function isValidEsiidDigits(digits: string): boolean {
  return /^\d+$/u.test(digits) && digits.length >= ESIID_MIN_DIGITS && digits.length <= ESIID_MAX_DIGITS;
}
